import store from '../../../../state';
import { ForecastWidgetConfig, WidgetRenderContext, WidgetRenderer } from '../types';
import { buildLocalCalendarDayBuckets, resolveSourceBadge } from './DailyForecastListWidget';
import { ForecastBinaryService } from '../../../../services/ForecastBinaryService';
import TierAccessService from '../../../../services/TierAccessService';

export interface DailyPrecipSummary {
  dayLabel: string;
  dateSub: string;
  icon: string;
  condition: string;
  precipIn: number;
  precipDisplay: string;
  rainPct: number;
  peakRateInH: number;
}

export function aggregateDailyPrecip(ctx: WidgetRenderContext, overrideDays?: number | string): DailyPrecipSummary[] {
  const data = ctx.data;
  if (!data || !data.tempsForecast || data.tempsForecast.length === 0) {
    return [];
  }

  const isImperial = store.getState().units === 'imperial';
  const precip = data.precipLikelihood || [];
  const rainRate = data.precipRate || [];

  const buckets = buildLocalCalendarDayBuckets(ctx, overrideDays);
  const summaries: DailyPrecipSummary[] = [];

  for (const bucket of buckets) {
    if (bucket.hourIndices.length === 0) continue;

    const slicePrecip = bucket.hourIndices.map(i => precip[i] ?? 0);
    const sliceRate = bucket.hourIndices.map(i => rainRate[i] ?? 0);

    const maxRainPct = slicePrecip.length ? Math.round(Math.max(...slicePrecip)) : 0;
    const peakRate = sliceRate.length ? Math.max(...sliceRate) : 0;
    // Hourly rate in in/hr summed over 1h steps gives total daily inches
    const rawDailyIn = sliceRate.reduce((acc, r) => acc + (r > 0 ? r : 0), 0);
    const roundedIn = Math.round(rawDailyIn * 100) / 100;

    let precipDisplay: string;
    if (isImperial) {
      precipDisplay = roundedIn >= 0.01 ? `${roundedIn.toFixed(2)}″` : (maxRainPct >= 20 ? 'Trace' : '0.00″');
    } else {
      const mm = Math.round(roundedIn * 25.4 * 10) / 10;
      precipDisplay = mm >= 0.1 ? `${mm.toFixed(1)} mm` : (maxRainPct >= 20 ? 'Trace' : '0.0 mm');
    }

    let icon = '☀️';
    let condition = 'Dry';
    if (roundedIn >= 0.75 || peakRate >= 0.20) {
      icon = '🌧️';
      condition = 'Heavy Rain';
    } else if (roundedIn >= 0.20 || maxRainPct >= 60) {
      icon = '🌧️';
      condition = 'Rain Likely';
    } else if (roundedIn >= 0.05 || maxRainPct >= 35) {
      icon = '🌦️';
      condition = 'Showers';
    } else if (maxRainPct >= 20 || roundedIn > 0) {
      icon = '🌦️';
      condition = 'Slight Chance';
    } else {
      icon = '☀️';
      condition = 'Dry';
    }

    summaries.push({
      dayLabel: bucket.dayLabel,
      dateSub: bucket.dateSub,
      icon,
      condition,
      precipIn: roundedIn,
      precipDisplay,
      rainPct: maxRainPct,
      peakRateInH: Math.round(peakRate * 100) / 100
    });
  }

  return summaries;
}

export class DailyPrecipWidget implements WidgetRenderer {
  public renderHtml(widget: ForecastWidgetConfig, ctx: WidgetRenderContext): string {
    const title = widget.title || 'Daily Precipitation Forecast';
    const optPeriod = widget.options?.period || 'auto';
    const requestedHorizon = optPeriod !== 'auto' ? optPeriod : ctx.viewDays;
    const days = aggregateDailyPrecip(ctx, requestedHorizon);

    const lat = ctx.lat;
    const lon = ctx.lon;
    const isCA = lat >= 41.6 && lat <= 83.1 && lon >= -141.0 && lon <= -52.6 && (lat >= 49.0 || (lat >= 42.0 && lon < -79.0));
    const isUS = (lat >= 24.5 && lat <= 49.38 && lon >= -124.8 && lon <= -66.9) ||
                 (lat >= 51.0 && lat <= 71.5 && lon >= -179.0 && lon <= -129.0) ||
                 (lat >= 18.9 && lat <= 22.3 && lon >= -160.3 && lon <= -154.8);
    const autoAgency = isUS ? 'NWS' : (isCA ? 'ECCC' : 'ECMWF');

    const activeSourceKey = (widget.options?.dataSource && widget.options.dataSource !== 'auto')
      ? widget.options.dataSource
      : (ctx.activeTabId && ctx.activeTabId !== 'blend' ? ctx.activeTabId : 'auto');

    const sourceBadge = resolveSourceBadge(ctx, activeSourceKey);
    const isImperial = store.getState().units === 'imperial';

    const activeH = String(requestedHorizon).toLowerCase();
    const is3 = activeH.startsWith('3');
    const is5 = activeH.startsWith('5');
    const is7 = activeH.startsWith('7');
    const isMax = activeH === 'max' || activeH === '10';

    // Summary statistics
    const totalIn = days.reduce((sum, d) => sum + d.precipIn, 0);
    const maxDay = days.reduce((max, d) => (d.precipIn > max.precipIn ? d : max), days[0] || { dayLabel: '--', precipIn: 0 });
    const wetDays = days.filter(d => d.precipIn >= 0.01).length;

    const totalDisplay = isImperial
      ? `${totalIn.toFixed(2)}″`
      : `${(Math.round(totalIn * 25.4 * 10) / 10).toFixed(1)} mm`;

    const maxDayDisplay = isImperial
      ? `${maxDay?.precipIn.toFixed(2)}″`
      : `${(Math.round((maxDay?.precipIn || 0) * 25.4 * 10) / 10).toFixed(1)} mm`;

    const maxPrecipVal = days.length ? Math.max(0.1, ...days.map(d => d.precipIn)) : 1.0;

    const showSummaryBanner = widget.options?.showSummaryBanner !== false;

    const rowsHtml = days.map(d => {
      const barWidthPct = d.precipIn > 0
        ? Math.min(100, Math.max(5, Math.round((d.precipIn / maxPrecipVal) * 100)))
        : 0;

      const rainProbHtml = d.rainPct > 0
        ? `<span style="font-size: 0.72rem; font-weight: 700; color: #00d4ff; font-family: monospace; display: inline-flex; align-items: center; gap: 2px;">💧${d.rainPct}%</span>`
        : `<span style="font-size: 0.72rem; color: #64748b; opacity: 0.4;">--</span>`;

      return `
        <div style="display: grid; grid-template-columns: 80px 28px 46px 1fr 64px; align-items: center; gap: 8px; padding: 7px 0; border-bottom: 1px solid rgba(255, 255, 255, 0.05); font-size: 0.8rem;">
          <div style="font-weight: 700; color: #f8fafc; line-height: 1.2;">
            <div>${d.dayLabel}</div>
            <div style="font-size: 0.65rem; color: #94a3b8; font-weight: 500;">${d.dateSub}</div>
          </div>
          <div style="font-size: 1.2rem; text-align: center; line-height: 1;" title="${d.condition}">${d.icon}</div>
          <div>${rainProbHtml}</div>
          <div style="display: flex; align-items: center; gap: 8px; min-width: 0;">
            <div style="flex: 1; height: 7px; background: rgba(255, 255, 255, 0.07); border-radius: 999px; position: relative; overflow: hidden;">
              ${barWidthPct > 0 ? `
              <div style="position: absolute; top: 0; bottom: 0; left: 0; width: ${barWidthPct}%; border-radius: 999px; background: linear-gradient(90deg, #0284c7 0%, #00d4ff 100%); box-shadow: 0 0 6px rgba(0, 212, 255, 0.35);"></div>
              ` : ''}
            </div>
          </div>
          <div style="font-size: 0.8rem; font-weight: 700; color: ${d.precipIn > 0 ? '#38bdf8' : '#94a3b8'}; text-align: right; font-family: monospace;">
            ${d.precipDisplay}
          </div>
        </div>
      `;
    }).join('');

    return `
      <div class="metric-card" style="background: linear-gradient(145deg, #101524 0%, #0c0e18 100%); border: 1px solid rgba(0, 212, 255, 0.2); border-radius: 14px; padding: 14px 16px; box-shadow: 0 8px 24px rgba(0,0,0,0.3);">
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; flex-wrap: wrap; gap: 8px;">
          <div style="display: flex; align-items: center; gap: 8px; flex-wrap: wrap;">
            <span style="font-size: 0.95rem; font-weight: 700; color: #fff;">🌧️ ${title}</span>
            ${widget.options?.allowSourceSelection !== false ? `
            <div style="display: inline-flex; align-items: center; gap: 4px;">
              <span style="font-size: 0.65rem; color: #94a3b8; font-weight: 600;">SOURCE:</span>
              <select class="daily-source-select" style="background: rgba(0, 212, 255, 0.12); color: #00d4ff; border: 1px solid rgba(0, 212, 255, 0.35); border-radius: 6px; padding: 2px 6px; font-size: 0.7rem; font-weight: 700; cursor: pointer; outline: none;">
                <option value="auto" ${activeSourceKey === 'auto' ? 'selected' : ''}>Auto (${autoAgency})</option>
                <option value="nws" ${activeSourceKey === 'nws' ? 'selected' : ''}>NWS (NOAA)</option>
                <option value="eccc" ${activeSourceKey === 'eccc' ? 'selected' : ''}>ECCC (Canada)</option>
                <option value="ecmwf" ${activeSourceKey === 'ecmwf' ? 'selected' : ''}>ECMWF (IFS)</option>
                <option value="icon13" ${activeSourceKey === 'icon13' ? 'selected' : ''}>ICON-13 (DWD)</option>
                <option value="hrrr" ${activeSourceKey === 'hrrr' ? 'selected' : ''}>HRRR (3km)</option>
                <option value="rrfs" ${activeSourceKey === 'rrfs' ? 'selected' : ''}>RRFS (3km)</option>
                <option value="rdps" ${activeSourceKey === 'rdps' ? 'selected' : ''}>RDPS (10km)</option>
                <option value="gdps" ${activeSourceKey === 'gdps' ? 'selected' : ''}>GDPS (15km)</option>
                <option value="gfs" ${activeSourceKey === 'gfs' ? 'selected' : ''}>GFS (0.25°)</option>
              </select>
            </div>
            ` : `
            <span style="font-size: 0.65rem; padding: 2px 6px; background: rgba(0, 212, 255, 0.12); color: #00d4ff; border-radius: 4px; font-weight: 700; border: 1px solid rgba(0, 212, 255, 0.25); letter-spacing: 0.3px;">${sourceBadge}</span>
            `}
          </div>
          <div class="daily-horizon-pills" style="display: inline-flex; background: rgba(0, 0, 0, 0.45); border-radius: 6px; padding: 2px; border: 1px solid rgba(255, 255, 255, 0.08);">
            <button class="daily-h-btn ${is3 ? 'active' : ''}" data-days="3" style="background: ${is3 ? '#00d4ff' : 'transparent'}; color: ${is3 ? '#090a0f' : '#94a3b8'}; border: none; font-size: 0.7rem; font-weight: 700; padding: 2px 7px; border-radius: 4px; cursor: pointer;">3D</button>
            <button class="daily-h-btn ${is5 ? 'active' : ''}" data-days="5" style="background: ${is5 ? '#00d4ff' : 'transparent'}; color: ${is5 ? '#090a0f' : '#94a3b8'}; border: none; font-size: 0.7rem; font-weight: 700; padding: 2px 7px; border-radius: 4px; cursor: pointer;">5D</button>
            <button class="daily-h-btn ${is7 ? 'active' : ''}" data-days="7" style="background: ${is7 ? '#00d4ff' : 'transparent'}; color: ${is7 ? '#090a0f' : '#94a3b8'}; border: none; font-size: 0.7rem; font-weight: 700; padding: 2px 7px; border-radius: 4px; cursor: pointer;">7D</button>
            <button class="daily-h-btn ${isMax ? 'active' : ''}" data-days="max" style="background: ${isMax ? '#00d4ff' : 'transparent'}; color: ${isMax ? '#090a0f' : '#94a3b8'}; border: none; font-size: 0.7rem; font-weight: 700; padding: 2px 7px; border-radius: 4px; cursor: pointer;">Max</button>
          </div>
        </div>

        ${showSummaryBanner && days.length > 0 ? `
        <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; background: rgba(2, 132, 199, 0.08); border: 1px solid rgba(0, 212, 255, 0.15); border-radius: 8px; padding: 8px 10px; margin-bottom: 12px; text-align: center;">
          <div>
            <div style="font-size: 0.65rem; color: #94a3b8; font-weight: 600; text-transform: uppercase;">Total Rain</div>
            <div style="font-size: 1.05rem; font-weight: 800; color: #38bdf8; font-family: monospace;">${totalDisplay}</div>
          </div>
          <div>
            <div style="font-size: 0.65rem; color: #94a3b8; font-weight: 600; text-transform: uppercase;">Peak Day</div>
            <div style="font-size: 0.95rem; font-weight: 700; color: #f8fafc; font-family: monospace;">${maxDay?.dayLabel || '--'} <span style="font-size: 0.75rem; color: #38bdf8;">(${maxDayDisplay})</span></div>
          </div>
          <div>
            <div style="font-size: 0.65rem; color: #94a3b8; font-weight: 600; text-transform: uppercase;">Wet Days</div>
            <div style="font-size: 1.05rem; font-weight: 800; color: #f8fafc; font-family: monospace;">${wetDays} <span style="font-size: 0.7rem; color: #94a3b8; font-weight: 500;">/ ${days.length}d</span></div>
          </div>
        </div>
        ` : ''}

        <div>
          ${rowsHtml || ((ctx.data?.accessDenial || ForecastBinaryService.lastAccessDenial || !TierAccessService.userHasLayerAccess('forecast_point_tiles'))
            ? TierAccessService.buildTrendChartDenialHtml({
                denial: ctx.data?.accessDenial || ForecastBinaryService.lastAccessDenial,
                requiredTier: 'pro',
                featureLabel: `${title} (${activeSourceKey.toUpperCase()})`
              })
            : '<div style="color: #94a3b8; font-size: 0.8rem; text-align: center; padding: 12px;">No precipitation forecast data available</div>')}
        </div>
      </div>
    `;
  }

  public init(widget: ForecastWidgetConfig, ctx: WidgetRenderContext): void {
    const container = document.querySelector(`[data-widget-id="${widget.id}"]`) || document;
    const buttons = container.querySelectorAll('.daily-h-btn');
    buttons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const d = (btn as HTMLElement).dataset.days;
        if (d && ctx.onHorizonChange) {
          ctx.onHorizonChange(d === 'max' ? 'max' : parseInt(d, 10));
        }
      });
    });

    const sourceSelect = container.querySelector('.daily-source-select') as HTMLSelectElement;
    if (sourceSelect) {
      sourceSelect.addEventListener('change', (e) => {
        e.stopPropagation();
        const val = sourceSelect.value;
        if (ctx.onSourceChange) {
          ctx.onSourceChange(val);
        }
      });
    }
  }
}
